import React, { useEffect, useRef, useState, useMemo } from "react";
import { scaleLinear, select } from "d3";
import ToggleButton from "../../../ui/ToggleButton/ToggleButton";
import CustomSliderComponent from "../../../ui/Slider";
import { formatNumberToKAndM } from "../../../utils/formatNumberToKandM";
import { FilterChartContainer, StatsValue, ToggleButtonContainer } from "../../../styled-components/ChartStyles";
import { SliderProps } from "@mui/material/Slider";

interface MatchLabel {
  key: string;
  value: string;
}
interface BarChartProps {
  header?: string;
  togglelabel?: MatchLabel[];
  data: any[];
  onDataUpdate: (min: number, max: number) => void;
  initialMinValue: number;
  initialMaxValue: number;
  defaultSelectedValue: [number, number];
  isFor?: string;
  onChange?: (selectedTab: any) => void;
  selectedLabel?: string;
  isDisplaySVG?: boolean;
}

const BarChart: React.FC<BarChartProps> = ({
  header,
  togglelabel,
  data,
  onDataUpdate,
  initialMinValue,
  initialMaxValue,
  defaultSelectedValue,
  isFor,
  onChange,
  selectedLabel,
  isDisplaySVG,
}) => {
  const [sliderValues, setSliderValues] = useState<Array<[number, number]>>([defaultSelectedValue]);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const keys = Object.keys(data[0]);
  const txtInputWidth = useMemo(
    () => (isFor === "Transfer Value" ? Math.max(50, initialMinValue?.toString().length * 20) : 70),
    [isFor, initialMinValue]
  );
  const txtInputMaxWidth = useMemo(
    () => (isFor === "Transfer Value" ? Math.max(50, initialMaxValue?.toString().length * 10) : 70),
    [isFor, initialMaxValue]
  );
  const [activeMinInput, setActiveMinInput] = useState<number | null>(null);
  const [activeMaxInput, setActiveMaxInput] = useState<number | null>(null);

  useEffect(() => {
    setSliderValues([defaultSelectedValue]); // Update slider values when default selected value changes
  }, [defaultSelectedValue]);

  useEffect(() => {
    if (!svgRef.current) return;
    const minCount = 0;
    const maxCount = Math.max(...data.map((item) => item.count ?? item.number));
    const svg = select(svgRef.current);
    const yScale = scaleLinear().domain([minCount, maxCount]).range([0, 100]);
    const width = svg.node()?.getBoundingClientRect().width ?? 0;
    const barWidth = width / data.length - 2;
    svg.selectAll("*").remove();
    keys.forEach((key, i) => {
      if (key !== keys[0]) {
        data.forEach((dataItem, j) => {
          svg
            .append("rect")
            .attr("height", yScale(dataItem[key]))
            .attr("width", barWidth)
            .style("fill", sliderValues[0][0] < dataItem["age"] && dataItem["age"] < sliderValues[0][1] ? "#C9F0B5" : "#EEFAE8")
            .attr("x", j * (width / data.length))
            .attr("y", 100 - yScale(dataItem[key]));
        });
      }
    });
  }, [data, keys]);

  const handleChange: SliderProps["onChange"] = (event, newValue, activeThumb) => {
    if (!Array.isArray(newValue)) {
      return;
    }
    setSliderValues((prevValues) => {
      const updatedValues: [number, number][] = [...prevValues];
      updatedValues[activeThumb] = newValue as [number, number];
      return updatedValues;
    });
  };

  const handleOnDataUpdate = (event: React.SyntheticEvent | Event, value: number | number[]) => {
    if (!Array.isArray(value)) {
      return;
    }
    onDataUpdate(value[0], value[1]);
  };

  const handleMinFocus = (index: number) => {
    setActiveMinInput(index);
  };

  const handleMinBlur = (event: React.FocusEvent<HTMLInputElement>, index: number) => {
    setActiveMinInput(null);
    const value = parseFloat(event.target.value);
    if (!isNaN(value)) {
      setSliderValues((prevValues) => {
        const updatedValues = [...prevValues];
        updatedValues[index][0] = value;
        return updatedValues;
      });
      onDataUpdate(value, sliderValues[index][1]);
    }
  };

  const handleMaxFocus = (index: number) => {
    setActiveMaxInput(index);
  };

  const handleMaxBlur = (event: React.FocusEvent<HTMLInputElement>, index: number) => {
    setActiveMaxInput(null);
    const value = parseFloat(event.target.value);
    if (!isNaN(value)) {
      setSliderValues((prevValues) => {
        const updatedValues = [...prevValues];
        updatedValues[index][1] = value;
        return updatedValues;
      });
      onDataUpdate(sliderValues[index][0], value);
    }
  };

  

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, index: number, isMinValue: boolean) => {
    const inputValue = parseFloat(event.target.value);
    if (isNaN(inputValue)) return;
    setSliderValues((prevValues) => {
      const updatedValues = [...prevValues];
      const currentRange = updatedValues[index];
      if (isMinValue) {
        updatedValues[index] = [Number(inputValue) || currentRange[0], currentRange[1]];
      } else {
        updatedValues[index] = [currentRange[0], Number(inputValue) || currentRange[1]];
      }
      return updatedValues;
    });
  };

  // const handleInputChange = (
  //   event: React.ChangeEvent<HTMLInputElement>, 
  //   index: number, 
  //   isMinValue: boolean
  // ) => {
  //   const inputValue = event.target.value;
  
  //   // Allow only valid decimal numbers
  //   if (!/^\d*\.?\d*$/.test(inputValue)) return;
  
  //   setSliderValues((prevValues) => {
  //     const updatedValues = [...prevValues];
  //     const currentRange = updatedValues[index];
  
  //     if (isMinValue) {
  //       updatedValues[index] = [parseFloat(inputValue) || currentRange[0], currentRange[1]];
  //     } else {
  //       updatedValues[index] = [currentRange[0], parseFloat(inputValue) || currentRange[1]];
  //     }
  
  //     return updatedValues;
  //   });
  // };
  

  return (
    <FilterChartContainer>
      {header && <div className="header">{header}</div>}
      {togglelabel && (
        <ToggleButtonContainer>
          <ToggleButton labels={togglelabel || []} defaultLabelKey={selectedLabel!} onChange={onChange} />
        </ToggleButtonContainer>
      )}
      <div className="silder-filter">
        {keys.slice(1).map((key, index) => (
          <div key={index}>
            {isDisplaySVG && (
              <div style={{ maxHeight: "90px" }}>
                <svg ref={svgRef} style={{ width: "100%" }}></svg>
              </div>
            )}
            <CustomSliderComponent
              value={sliderValues[index]}
              min={initialMinValue}
              max={initialMaxValue}
              step={isFor === "Transfer Value" ? 5000 : isFor === "Assets" ? 0.01 : 1}
              onChange={(event, newValue) => handleChange(event, newValue, index)}
              onDataUpdate={(event, value) => handleOnDataUpdate(event, value)}
            />
            <StatsValue key={index}>
              <div className="inputfiled-container">
                <input
                  className="barchart-inputfiled"
                  id="inputField"
                  type="text"
                  value={
                    activeMinInput === index
                      ? sliderValues[index][0]
                      : isFor === "Transfer Value"
                      ? formatNumberToKAndM(sliderValues[index][0])
                      : sliderValues[index][0]
                  }
                  onFocus={() => handleMinFocus(index)}
                  onBlur={(event) => handleMinBlur(event, index)}
                  onChange={(event) => handleInputChange(event, index, true)}
                  style={{
                    // width: `${txtInputWidth}px`,
                    transition: "width 0.2s ease",
                  }}
                />
              </div>
              <input
                className="barchart-inputfiled"
                id={`maxInput-${index}`}
                type="text"
                value={
                  activeMaxInput === index
                    ? sliderValues[index][1] // Show raw value if active
                    : isFor === "Transfer Value"
                    ? formatNumberToKAndM(sliderValues[index][1]) // Show formatted value
                    : sliderValues[index][1]
                }
                onFocus={() => handleMaxFocus(index)} // Max field focus
                onBlur={(event) => handleMaxBlur(event, index)} // Max field blur
                onChange={(event) => handleInputChange(event, index, false)} // Handle input change
                style={{
                  // width: `${txtInputMaxWidth}px`,
                  transition: "width 0.2s ease",
                }}
              />
            </StatsValue>
          </div>
        ))}
      </div>
    </FilterChartContainer>
  );
};

export default React.memo(BarChart);
